<template>
  <div class="field-token-list">
    <!-- Search Header -->
    <div class="field-token-list__header">
      <div class="field-token-list__search">
        <v-text-field
          v-model="searchQuery"
          placeholder="Search fields..."
          prepend-inner-icon="mdi-magnify"
          clearable
          dense
          outlined
          hide-details
          :disabled="disabled"
        />
      </div>
    </div>

    <!-- Field List -->
    <div class="field-token-list__content">
      <div class="field-token-list__fields">
        <FieldItem
          v-for="field in filteredFields"
          :key="field.fieldName"
          :field="field"
          :disabled="disabled"
          :is-used="fieldUsageMap[field.fieldName] > 0"
          :usage-count="fieldUsageMap[field.fieldName] || 0"
          @click="handleFieldClick"
          @dragstart="handleDragStart"
          @dragend="handleDragEnd"
          v-bind="$attrs"
        />
      </div>

      <!-- No Results -->
      <div v-if="filteredFields.length === 0" class="field-token-list__empty">
        <v-icon color="grey" size="48">mdi-magnify</v-icon>
        <p class="text-caption text-center mt-2">
          No fields found matching "{{ searchQuery }}"
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import { VTextField } from 'vuetify/lib/components'
import FieldItem from './FieldItem.vue'

interface CassieField {
  fieldName: string
  displayName: string
  typeFriendlyName: string
  dataType: string
  isFilterable: boolean
  isIncremental: boolean
}


export default Vue.extend({
  name: 'FieldTokenList',
  inheritAttrs: false,
  components: {
    VTextField,
    FieldItem
  },
  props: {
    fields: {
      type: Array as () => CassieField[],
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    currentPayload: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      searchQuery: '',
      // Memoization cache for fieldUsageMap performance
      usageMapCache: {} as Record<string, number>,
      lastPayloadHash: '',
      lastFieldsHash: ''
    }
  },
  computed: {

    /**
     * Get sorted fields without grouping
     */
    sortedFields(): CassieField[] {
      return [...this.fields].sort((a, b) => a.displayName.localeCompare(b.displayName))
    },

    /**
     * Filter fields based on search query
     */
    filteredFields(): CassieField[] {
      if (!this.searchQuery.trim()) {
        return this.sortedFields
      }

      const query = this.searchQuery.toLowerCase()
      return this.sortedFields.filter(field => 
        field.displayName.toLowerCase().includes(query) ||
        field.fieldName.toLowerCase().includes(query) ||
        field.typeFriendlyName.toLowerCase().includes(query)
      )
    },

    /**
     * Analyze current payload to count field usage (memoized for performance)
     */
    fieldUsageMap(): Record<string, number> {
      // Generate hash keys for caching
      const payloadHash = this.currentPayload ? this.currentPayload.slice(0, 100) : '';
      const fieldsHash = this.fields ? JSON.stringify(this.fields.map(f => f.fieldName)).slice(0, 100) : '';
      
      // Check if cache is still valid
      if (payloadHash === this.lastPayloadHash && fieldsHash === this.lastFieldsHash) {
        return this.usageMapCache;
      }
      
      // Cache is invalid, recalculate
      const usageMap: Record<string, number> = {}
      
      if (!this.currentPayload) {
        this.usageMapCache = usageMap;
        this.lastPayloadHash = payloadHash;
        this.lastFieldsHash = fieldsHash;
        return usageMap;
      }

      // Look for field placeholders in the format [PLACEHOLDER_FIELDNAME]
      this.fields.forEach(field => {
        const fieldPart = field.fieldName.split('.').pop() || field.fieldName
        const cleanFieldName = fieldPart.replace(/_/g, '').toUpperCase()
        const placeholder = `[PLACEHOLDER_${cleanFieldName}]`
        
        // Count occurrences of this placeholder in the payload (both quoted and unquoted)
        const escapedPlaceholder = placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const regex = new RegExp(`"?${escapedPlaceholder}"?`, 'g')
        const matches = this.currentPayload.match(regex)
        
        if (matches) {
          usageMap[field.fieldName] = matches.length
        }
      })

      // Update cache
      this.usageMapCache = usageMap;
      this.lastPayloadHash = payloadHash;
      this.lastFieldsHash = fieldsHash;

      return usageMap
    }
  },
  methods: {

    /**
     * Handle field click
     */
    handleFieldClick(field: CassieField): void {
      if (this.disabled) return
      this.$emit('field-click', field)
    },

    /**
     * Handle drag start
     */
    handleDragStart(field: CassieField): void {
      this.$emit('field-drag-start', field)
    },

    /**
     * Handle drag end
     */
    handleDragEnd(): void {
      this.$emit('field-drag-end')
    },

    /**
     * Clone field for drag operation
     */
    cloneField(field: CassieField): CassieField {
      return { ...field }
    }
  }
})
</script>

<style lang="scss" scoped>
.field-token-list {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fafafa;

  &__header {
    padding: 16px;
    border-bottom: 1px solid #edebeb;
    flex-shrink: 0;
  }

  &__search {
    .v-text-field {
      font-size: 14px;
    }
  }

  &__content {
    flex: 1;
    overflow-y: auto;
    padding: 8px 0;
    min-height: 0; /* Allow flex children to shrink */
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 200px;
    color: #565a61;
  }

  &__fields {
    padding: 0;
  }
}

</style>